import {Location} from "@angular/common";
import {Constants} from "../constants";
import {IProduct, IProductGroup} from "./product-models";
import * as _ from 'lodash';
import {BaseComponent} from "../base.component";
import {BreadcrumbType} from "../breadcrumb/breadcrumb.models";
import {CartUtil} from "../cart/cart-util";
import {LoggerService} from "../common/logger.service";
import {UserService} from "../common/user.service";
import {BreadcrumbService} from "../breadcrumb/breadcrumb.service";
import {MarkupService} from "../common/markup.service";
import {AnalyticsService} from "../analytics/analytics.service";
import {JsonLdService} from "../seo/json-ld.service";
import {INavDisplay, ITag} from "../cart/catalog/catalog-models";
import {ProductReviewService} from "../productreview/product-review.service";
import {Component, computed, OnDestroy, OnInit, signal} from "@angular/core";
import {ContentStackLoadingStatus} from '@usana/ux/cms';
import {ProductService} from "./product.service";
import {IProductSummary} from "../productreview/product-review.models";
import {from, Subscription, throwError} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {GqlCartProductService} from "./graphql/gql-cart-product.service";
import {ProductUtil} from "../util/product-util";
import {catchError, finalize, switchMap, tap} from "rxjs/operators";
import {IShopUser} from "../session/customer-models";
import {LocaleSwitcherService} from "../locale/locale-switcher.service";
import {CmsContentStackLocaleService} from '@usana/ux/cms';
import {LocaleUtil} from "../util/locale-util";

@Component({
    selector: 'product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss'],
    standalone: false
})
export class ProductComponent extends BaseComponent implements OnInit, OnDestroy {

    productGroup: IProductGroup;
    product: IProduct;
    isLoading: boolean = false;
    recommendedProducts: IProduct[];
    bodyBenefitTags: INavDisplay[];
    productNumber: string;
    usanaProductId: string;
    notFound: boolean = false;
    jsonLd: any = {};
    productReviewSubscription: Subscription;
    isCelavive: boolean = false;
    showRecommendedProductsAiMessage: boolean = false;

    useCms = signal<boolean>(false);
    cmsStatus = signal<ContentStackLoadingStatus>(ContentStackLoadingStatus.LOADING);
    showLegacyPdp = computed(() => !this.useCms() || this.cmsStatus() === ContentStackLoadingStatus.NOT_FOUND);

    constructor(private readonly productService: ProductService,
                private readonly loggerService: LoggerService,
                private readonly userService: UserService,
                private readonly breadcrumbService: BreadcrumbService,
                private readonly analyticsService: AnalyticsService,
                private readonly productReviewService: ProductReviewService,
                private readonly jsonLdService: JsonLdService,
                private readonly markupService: MarkupService,
                private readonly gqlCartProductService: GqlCartProductService,
                private readonly location: Location,
                private readonly router: Router,
                private readonly route: ActivatedRoute,
                private localeSwitcherService: LocaleSwitcherService,
                private readonly cmsLocaleService: CmsContentStackLocaleService
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.route.paramMap.subscribe((param) => {
            this.isLoading = true;
            this.onCmsStatus(ContentStackLoadingStatus.LOADING);

            this.loadUsanaId(param.get('usanaId'));
            this.initProduct(param.get('usanaId'), param.get('readableUrl'), param.get('locale'));
        });
    }

    ngOnDestroy() {
        if (this.productReviewSubscription != null) {
            this.productReviewSubscription.unsubscribe();
        }
    }

    onCmsStatus(status: ContentStackLoadingStatus) {
        // Show original pdp if not found
        this.cmsStatus.set(status);
        this.log.debug('CMS status:', status);
    }

    setBreadcrumb() {
        this.breadcrumbService.setBreadcrumb(this.product.id, BreadcrumbType.PRODUCT);
    }

    canAddToAutoOrder() {
        return CartUtil.showAddToAoButtonProduct(this.product);
    }

    initProduct(usanaIdParam: string, readableUrlParam: string, locale: string) {
        from(this.getCustomerData()).pipe(
            tap((user: IShopUser) => {
                const locale = user?.usanaLocale ?? Constants.DEFAULT_LOCALE;
                // This sets the locale for the CMS requests and is looking for a "-" in the locale string.
                // Needs to be set before this.useCms is set to true.
                this.cmsLocaleService.setLocale(CmsContentStackLocaleService.transformIsoCode(LocaleUtil.getUrlLocale(locale)));
                this.useCms.set(user?.config?.useCms)
            }),
            switchMap(() => this.gqlCartProductService.getCartPDP(this.usanaProductId, readableUrlParam)),
            catchError((error: any) => throwError(error)),
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: (data: IProductGroup) => {
                if (data) {
                    this.productGroup = data;
                    this.log.debug("getCartPDP", data.id, data);

                    //load matching product or first product available
                    if (!!this.productGroup.products && Object.values(this.productGroup.products).length > 0) {
                        this.product = ProductUtil.findMatchedProduct(this.usanaProductId, Object.values(this.productGroup.products));
                        //if no matched product found grab first product, because 3.6 might not match when crossing countries
                        //and if a product was returned without matching that means it was in the same cross market group and
                        //should be the correct product for the users market
                        if (!this.product && this.productGroup.products) {
                            this.product = this.productGroup.products[0];
                        }
                    }

                    //if the fuzzy search is used, then we need to state transition to it and update the url with the new info
                    this.updateRoute(usanaIdParam, readableUrlParam, locale);

                    if (this.showProductReviews) {
                        this.productReviewService.initProductReviews(this.product.id, this.product.usanaId);
                    }

                    this.setBodyBenefits();
                    this.showRecommendedProductsAiMessage = this.userService.getShopUserSnapshot().config.useBiRecommendedProducts;
                    this.getRecommendedProducts(this.product.id, this.product.usanaId);
                    this.markupService.setOgSingleProductPage(this.product);
                    this.setBreadcrumb();
                    this.markupService.setTitle(this.product.productDescription.name + Constants.USANA_PAGE_TITLE_ENDING);
                    this.log.debug("getCartPDP, Product", this.product.id, this.product);
                    this.productService.prepareProductGroup(this.productGroup, this.product.id);
                    this.analyticsService.viewProductEvent(this.product);
                    this.isCelavive = CartUtil.isCelaviveCategory(this.product.productCategory.name);
                    this.getJsonLd();
                } else {
                    this.notFound = true;
                    this.markupService.setTitleFromTranslationTag("cart.product.missing.title");
                }
            },
            error: (error) => {
                this.log.error("getCartPDP", error);
                this.notFound = true;
            }
        });
    }

    updateRoute(usanaIdParam: string, readableUrlParam: string, locale:string) {
        if (this.product.usanaId != this.usanaProductId || usanaIdParam.indexOf(".") > -1 || readableUrlParam !== this.productGroup.seoCrossMarketUrl) {
            const seoUsanaId = this.product.usanaId.replace(/\./g, "-");// regex replace "." globally

            if (!!this.productGroup.seoCrossMarketUrl && this.productGroup.seoCrossMarketUrl.length > 0) {
                // router.navigate adds instead of replacing the current entry in the browser history,
                // even when using the replaceUrl parameter.
                // Instead, we have to use location.replaceState in order to maintain expected browser back button behavior
                this.location.replaceState(this.router.serializeUrl(this.router.createUrlTree([locale, Constants.PRODUCT_STATE, seoUsanaId, this.productGroup.seoCrossMarketUrl])));
            } else {
                // see note above about router.navigate and location.replaceState
                this.location.replaceState(this.router.serializeUrl(this.router.createUrlTree([locale, Constants.PRODUCT_STATE, seoUsanaId])));
            }
        }
    }

    loadUsanaId(usanaId: string) {
        //seo wants "-" in url instead of "."
        //remove seo characters and store the value
        if (usanaId.indexOf('-') > -1) {
            this.usanaProductId = usanaId.replace(/-/g, '.'); //regex replace "-" globally
        } else {
            this.usanaProductId = usanaId;
        }

        //check the stored value and get the part number
        if ((this.usanaProductId.match(new RegExp("\\.", 'g')) || []).length > 1) {
            //if id has more than 1 "." (oola and rise) we will just use the whole thing
            this.productNumber = this.usanaProductId;
        } else if (this.usanaProductId.indexOf('.') > -1) {
            this.productNumber = this.usanaProductId.split('.')[0];
        } else {
            this.productNumber = this.usanaProductId.substring(0, 3);
        }
    }

    getJsonLd() {
        if (this.showProductReviews) {
            this.productReviewSubscription= this.productReviewService.productReviewObservable.subscribe((productReview: any) => {
                this.jsonLd = this.jsonLdService.getProductJson(this.product, productReview);
            });
        } else {
            this.jsonLd = this.jsonLdService.getProductJson(this.product, new IProductSummary());
        }
    }

    getRecommendedProducts(productId: number, usanaId: string) {
        if (this.limitedShopping) { // limited shopping experience does not need to fetch product recommends
            this.recommendedProducts = [];
        } else {
            this.gqlCartProductService.getRecommendedProducts(productId, usanaId).subscribe({
                next: (data: IProduct[]) => {
                    this.log.debug("getRecommendedProducts", data);
                    this.recommendedProducts = data ?? [];
                },
                error: (error) => {
                    this.log.error("getRecommendedProducts", error);
                    this.recommendedProducts = [];
                }
            })
        }
    }

    setBodyBenefits() {
        this.log.debug("getProductTags", this.product.productTags);
        this.bodyBenefitTags = _.filter(this.product.productTags, (tag: ITag) => {
            return !!tag.pdpDisplay && !!tag.parent && Constants.BODY_BENEFIT_TAG_NAME == tag.parent.name;
        }).map(tag => {
            return CartUtil.buildNavDisplay(tag);
        });
    }

    openLocaleSwitcher() {
        this.localeSwitcherService.openLocaleSwitcher();
    }
}
